/**
 * Report Artifact Page
 *
 * Displays a generated report artifact for a completed session.
 * Route: /prioritization/sessions/:sessionId/reports/:artifactId
 *
 * Source: DOCS_V4/00-canon/10O-cursor-session-results-explanation-and-reports.md
 */

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Users,
  ListOrdered,
  Sparkles,
  GitMerge,
  Settings,
  AlignLeft,
  Phone,
  Loader2,
  AlertCircle,
  Clock,
  Printer,
  RefreshCw,
} from 'lucide-react';
import { fetchReportArtifact, writePrioritizationMemo } from '../../../lib/reportGenerationService';
import type { ReportArtifact, ReportSection } from '../../../lib/reportGenerationService';
import { isPrioritizationMemo } from '../../../lib/prioritizationMemo';
import { PrioritizationMemoBody } from '../../../components/prioritization/memo/PrioritizationMemoBody';

// ── Section icon map ──────────────────────────────────────────────────────────

const SECTION_ICONS: Record<string, React.ElementType> = {
  headline: AlignLeft,
  context_header: AlignLeft,
  ranked_list: ListOrdered,
  ai_narrative: Sparkles,
  relationship_notes: GitMerge,
  session_metadata: Settings,
  text: AlignLeft,
  contact: Phone,
};

// ── Section renderer ──────────────────────────────────────────────────────────

function SectionCard({ section }: { section: ReportSection & { content?: string } }) {
  const Icon = SECTION_ICONS[section.type] ?? AlignLeft;
  const isEmpty = !section.content?.trim();

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
        <h3 className="text-sm font-semibold text-[var(--text-sec)]">{section.label}</h3>
        {section.type === 'ai_narrative' && (
          <span className="ml-1 inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] font-medium">
            <Sparkles className="h-3 w-3" />
            AI
          </span>
        )}
      </div>

      {isEmpty ? (
        <p className="text-sm text-[var(--text-ter)] italic">
          {section.type === 'text' || section.type === 'contact'
            ? 'Complete this section before sharing.'
            : 'No content generated for this section.'}
        </p>
      ) : (
        <div className="text-sm text-[var(--text-pri)] leading-relaxed whitespace-pre-line">
          {section.content}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

/** The memo's page: the memo, with Print and Write it again. */
function MemoView({ artifact, sessionId }: { artifact: ReportArtifact; sessionId: string }) {
  const navigate = useNavigate();
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!isPrioritizationMemo(artifact.content)) return null;
  const rewrite = async () => {
    setWriting(true);
    setError(null);
    const res = await writePrioritizationMemo(sessionId);
    setWriting(false);
    if (!res.ok || !res.artifact) {
      setError(res.error ?? 'The memo could not be written.');
      return;
    }
    navigate(`/prioritization/sessions/${sessionId}/reports/${res.artifact.id}`);
  };
  const btn =
    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors disabled:opacity-60';
  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to={`/prioritization/sessions/${sessionId}`}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to session
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/prioritization/sessions/${sessionId}/reports/${artifact.id}/print`} className={btn} data-testid="print-memo">
            <Printer className="h-3.5 w-3.5" />
            Print or save as PDF
          </Link>
          <button type="button" onClick={() => void rewrite()} disabled={writing} className={btn} data-testid="rewrite-memo">
            {writing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {writing ? 'Writing…' : 'Write it again'}
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-8">
        <PrioritizationMemoBody memo={artifact.content} generatedAt={artifact.generatedAt} />
      </div>
    </div>
  );
}

export default function ReportArtifactPage() {
  const { sessionId, artifactId } = useParams<{ sessionId: string; artifactId: string }>();
  const [artifact, setArtifact] = useState<ReportArtifact | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!artifactId) return;
    setIsLoading(true);
    fetchReportArtifact(artifactId)
      .then(a => {
        if (!a) setError('Report not found.');
        else setArtifact(a);
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load report.'))
      .finally(() => setIsLoading(false));
  }, [artifactId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
      </div>
    );
  }

  if (error || !artifact) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-[var(--coral)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[var(--coral)]">{error ?? 'Report not found.'}</p>
            <Link
              to={`/prioritization/sessions/${sessionId}`}
              className="mt-2 inline-flex items-center gap-1 text-sm text-[var(--coral)] hover:text-[var(--coral)]"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to session
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isPrioritizationMemo(artifact.content) && sessionId) {
    return <MemoView artifact={artifact} sessionId={sessionId} />;
  }

  const content = artifact.content as {
    templateName?: string;
    templateSlug?: string;
    audience?: string;
    sections?: Array<ReportSection & { content?: string }>;
    explanationHeadline?: string;
    keyInsights?: string[];
  };

  const sections = content.sections ?? [];

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-6">
      {/* Back nav */}
      <Link
        to={`/prioritization/sessions/${sessionId}`}
        className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to session
      </Link>

      {/* Header */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-[var(--slate-dim)] flex items-center justify-center">
            <FileText className="h-5 w-5 text-[var(--text-ter)]" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-[var(--text-pri)] leading-tight">{artifact.title}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              {content.audience && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] font-medium">
                  <Users className="h-3 w-3" />
                  {content.audience}
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)]">
                <Clock className="h-3 w-3" />
                Generated {artifact.generatedAt.toLocaleDateString()} at{' '}
                {artifact.generatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${
                artifact.status === 'published'
                  ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                  : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
              }`}>
                {artifact.status === 'published' ? 'Published' : 'Draft'}
              </span>
            </div>
          </div>
        </div>

        {/* Key insights if present */}
        {content.keyInsights && content.keyInsights.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[var(--rule)]">
            <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">Key Insights</p>
            <ul className="space-y-1">
              {content.keyInsights.map((insight, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-sec)]">
                  <span className="text-[var(--text-ter)] flex-shrink-0 mt-1">·</span>
                  {insight}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {sections.map(section => (
          <SectionCard key={section.id} section={section} />
        ))}
      </div>

      {/* Footer */}
      <p className="text-xs text-[var(--text-ter)] text-center pb-4">
        AI-generated content is advisory only. Results determined by the prioritization method and participant scores.
      </p>
    </div>
  );
}
